/**
 * Acequia Dependencies
 * Context-aware exports for browser, Node.js, and Deno
 *
 * Static imports are used for browser modules (bundled by rollup).
 * Node.js/Deno modules are loaded via initRuntime() to avoid top-level await,
 * which is disallowed in service workers.
 */

// Static imports for browser - these get bundled by rollup
import localforageModule from './web_modules/localforage.js'
import { createId as createIdBrowser } from './web_modules/@paralleldrive/cuid2.js'
import slugifyBrowser from './web_modules/slugify.js'
import QRiousBrowser from './web_modules/qrious.js'

// Detect runtime environment (check Deno before Node.js since Deno can have process polyfills)
const isDeno = typeof Deno !== 'undefined'
const isNode = !isDeno && typeof process !== 'undefined' && process.versions?.node

// Browser/SW defaults - set synchronously (no top-level await)
let localforage = localforageModule
let createId = createIdBrowser
let slugify = slugifyBrowser
let QRious = isDeno || isNode ? null : QRiousBrowser

/**
 * Initialize runtime-specific dependencies for Node.js/Deno.
 * Must be called before using exports in non-browser contexts.
 * No-op in browser/SW context.
 */
export async function initRuntime() {
    if (isDeno) {
        const { createLocalforageAdapter } = await import('./acequia/deno/storage.js')
        localforage = createLocalforageAdapter(Deno.env.get('ACEQUIA_STORAGE') || './acequia-data')
        createId = (await import('npm:@paralleldrive/cuid2')).createId
        slugify = (await import('npm:slugify')).default
    } else if (isNode) {
        const { createLocalforageAdapter } = await import('./acequia/node/storage.js')
        localforage = createLocalforageAdapter(process.env.ACEQUIA_STORAGE || './acequia-data')
        createId = (await import('@paralleldrive/cuid2')).createId
        slugify = (await import('slugify')).default
        // WebRTC: the vendored pure-JS stack (acequia/node/webrtc.js), only
        // when a peer will need it. Tolerant: without it the relay-proxied
        // path still works, and a peer connection is refused with a reason.
        if (!/^(0|false|no)$/i.test(process.env.ACEQUIA_NODE_WEBRTC_ENABLED || '')) {
            try {
                wrtc = await (await import('./acequia/node/webrtc.js')).loadNodeWebRTC()
            } catch (e) {
                console.warn(`[acequia] WebRTC unavailable in this Node process: ${e.message}`)
            }
        }
    }
}

// simple-peer's `wrtc` option in Node — null in a browser, where the real
// classes are globals. A live binding: set by initRuntime().
let wrtc = null

export { localforage, createId, slugify, QRious, wrtc }

// jose - works in all environments (webapi version uses Web Crypto API)
export * as jose from './node_modules/jose/dist/webapi/index.js'

// age (typage) - sealed envelopes; works in all environments. Accepts
// non-extractable WebCrypto X25519 CryptoKeys as identities.
export * as age from 'age-encryption'

// webdav - works in all environments
export * as webdav from './webdav.js'
